import { useEffect, useRef, useState } from 'react';
import DebtDroneMark from '@/components/icons/DebtDroneMark';
import { cn } from '@/lib/utils';

interface AuthSplashScreenProps {
  isLoading: boolean;
  children: React.ReactNode;
}

// Don't flash the splash for near-instant loads; once shown, keep it long enough
// to read as intentional rather than a flicker.
const SHOW_DELAY = 180;
const MIN_VISIBLE = 850;
const EXIT_MS = 650;

export function AuthSplashScreen({ isLoading, children }: AuthSplashScreenProps) {
  const [shouldRender, setShouldRender] = useState(true);
  const [show, setShow] = useState(false);
  const [entered, setEntered] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const shownAt = useRef<number | null>(null);

  // Delay before the overlay appears — fast loads never show it (no flicker).
  useEffect(() => {
    const t = setTimeout(() => {
      setShow(true);
      shownAt.current = Date.now();
    }, SHOW_DELAY);
    return () => clearTimeout(t);
  }, []);

  // Trigger the fade-in once the overlay is allowed to show.
  useEffect(() => {
    if (!show) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [show]);

  // When loading finishes, exit smoothly — honoring the minimum visible time.
  useEffect(() => {
    if (isLoading) return;

    // Loaded before the splash ever appeared → skip it entirely.
    if (!show) {
      setShouldRender(false);
      return;
    }

    const elapsed = Date.now() - (shownAt.current ?? Date.now());
    const wait = Math.max(0, MIN_VISIBLE - elapsed);
    let removeTimer: ReturnType<typeof setTimeout>;
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
      removeTimer = setTimeout(() => setShouldRender(false), EXIT_MS);
    }, wait);

    return () => {
      clearTimeout(exitTimer);
      if (removeTimer) clearTimeout(removeTimer);
    };
  }, [isLoading, show]);

  if (!shouldRender) {
    return <>{children}</>;
  }

  return (
    <>
      {show && (
        <div
          className={cn(
            'dark marketing-theme fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-background',
            'transition-all ease-out',
            isExiting ? 'duration-[650ms] opacity-0 -translate-y-2 blur-sm' : 'duration-700',
            entered && !isExiting ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0',
          )}
        >
          {/* Matte grid + soft top vignette */}
          <div className="pointer-events-none absolute inset-0 grid-bg opacity-50 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />

          <div className="relative flex flex-col items-center">
            {/* Brand mark with lime glow */}
            <div className="relative grid place-items-center">
              <div className="absolute h-40 w-40 rounded-full bg-lime/25 blur-3xl animate-glow-pulse" />
              <div className="relative z-10 h-24 w-24 sm:h-28 sm:w-28 animate-breathe">
                <DebtDroneMark className="h-full w-full" />
              </div>
            </div>

            {/* Wordmark */}
            <div
              className={cn(
                'mt-8 flex flex-col items-center transition-opacity duration-500',
                isExiting ? 'opacity-0' : 'opacity-100',
              )}
            >
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">DebtDrone</h1>
              <p className="mono mt-2 text-[12px] uppercase tracking-[0.18em] text-muted-foreground">
                Initializing workspace
              </p>

              {/* Indeterminate progress bar */}
              <div className="mt-5 h-0.5 w-40 overflow-hidden rounded-full bg-surface-2">
                <div className="h-full w-1/3 rounded-full bg-lime animate-loading-indeterminate" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/*
        Render children underneath so they are ready when the splash fades away.
      */}
      <div>{children}</div>
    </>
  );
}
